import { useEffect, useRef, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Grid, TextField, useTheme } from '@mui/material';
import { SearchAndSelect } from '@components';
import useSearchOfficials, { SearchResultOption } from '@hooks/useSearchOfficials';
import { createOperationActivity, OperationActivity } from '../../../../networkRequest/operationsService';
import { useUser } from '@hooks/useUser';

interface FormErrors {
  description: string | null;
}

const defaultFormErrors = {
  description: null,
};

const CreateActivity = ({ operationId, onSave, onError }: {
  operationId: string;
  onSave: (params: OperationActivity) => void
  onError: (error: string) => void;
}) => {
  const theme = useTheme();
  const { user } = useUser();
  const { searchResultOptions, handleSearch, parseOfficials } = useSearchOfficials({ operationId });
  const [activityDescription, setActivityDescription] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<SearchResultOption[]>([]);
  const [activityIsIncident, setActivityIsIncident] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormErrors>(defaultFormErrors);
  const [saving, setSaving] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus the text field on initial load and each time an activity is
  // saved. This allows the user to type in the text field, press enter to save
  // an activity, and keep typing a new message without having to click back
  // in the text field.
  useEffect(
    () => {
      if (!saving && inputRef.current !== null) {
        inputRef.current.focus();
      }
    },
    [ saving ]
  );

  const handleSelect = (_e, newValue: SearchResultOption[], reason: string) => {
    if (reason === 'clear' || !newValue.length) {
      setSelectedOptions([]);
    }

    if (!newValue) {
      return;
    }

    const parsedOptions = parseOfficials(newValue);
    setSelectedOptions(parsedOptions);
  };

  const clearFormData = () => {
    setActivityDescription('');
    setSelectedOptions([]);
    setActivityIsIncident(false);
    setFormErrors(defaultFormErrors);
  };

  const validateForm = (description: string) => {
    const isDescriptionValid = description.length > 0;
    setFormErrors({
      description: isDescriptionValid ? null : 'Description is required',
    });
    return isDescriptionValid;
  };

  const handleDescriptionChange = (value: string) => {
    setActivityDescription(value);
    validateForm(value);
  };

  const handleSave = async () => {
    if (!validateForm(activityDescription)) {
      return;
    }
    try {
      setSaving(true);
      const params = {
        description: activityDescription,
        official_ids: selectedOptions.map((official) => official.id),
        is_incident: activityIsIncident
      }

      const { data, error } = await createOperationActivity({ accessToken: user?.accessToken ?? '', operationId, params });
      
      if (data) {
        onSave(data);
        clearFormData();
        return
      }
      
      if (error) {
        onError(error)
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[50],
        marginTop: '32px',
        marginBottom: '16px',
        padding: '16px',
        borderRadius: '10px',
      }}
    >
      <Grid>
        <TextField
          inputRef={inputRef}
          disabled={saving}
          required
          multiline
          fullWidth
          label='New Activity'
          onChange={(e) => handleDescriptionChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSave();
            }
          }}
          value={activityDescription}
          error={formErrors.description !== null}
          sx={{
            marginBottom: '16px',
            padding: 0,
            '& textarea': {
              padding: 0,
            },
            '& .MuiInputBase-input': {
              padding: 0
            }
          }}
        />
      </Grid>
      <Grid>
        <SearchAndSelect
          label='Add Personnel'
          placeholder='Search for individuals or teams'
          options={searchResultOptions}
          onInputChange={handleSearch}
          value={selectedOptions}
          onChange={handleSelect}
        />
      </Grid>
      <Grid sx={{
        mt: '16px'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => setActivityIsIncident(e.target.checked)} checked={activityIsIncident}
            />
          }
          label='Mark activity as an incident'
        />
      </Grid>
      <Grid
        container
        sx={{
          justifyContent: 'flex-end',
          mt: '16px'
        }}>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </Grid>
    </Box>
  );
}

export default CreateActivity;